/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// component imports
import { Button, Card, Col, Form, Grid, Modal, Row, Tooltip, Typography } from "antd";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { FadeDivider } from "@/components/UI/FadeDivider";
import { SettingsFormElements } from "@/components/ProjectsComponents/Settings/SettingsFormElements";
import { ApiKeysSettings } from "@/components/ProjectsComponents/ApiKeys/ApiKeysSettings";
import { InfoCard } from "@/components/ProjectsComponents/InfoCard";

// misc imports
import { useGetProject } from "@/api/projects";
import { useServiceTypes, useTriggerCustomAction } from "@/api/services";
import { AUTH_FORM_LABEL_COL, AUTH_FORM_WRAPPER_COL } from "@/constants";
import { IServiceCustomActions, SERVICE_TYPE_SIMPLIFIED } from "@/interfaces/Service.interface";
import { SettingsFormStructuredData } from "@/interfaces/Service.interface";

const {useBreakpoint} = Grid;

/**
 * Component for rendering a form with project type settings.
 *
 * @component
 * @returns {JSX.Element} Rendered component
 */
export const ServiceTypeSettingsForm = () => {
  const screens = useBreakpoint();
  const isScreenBelow = (size: "sm" | "md" | "lg" | "xl" | "xxl") => screens.xs || (screens.sm && !screens[size]);
  const { t } = useTranslation();
  const { workspaceId, projectId } = useParams();
  const [form] = Form.useForm();
  const params = {
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  };

  const [fetchedSettings, setFetchedSettings] = useState<SettingsFormStructuredData>();
  const [customActions, setCustomActions] = useState<Record<string, IServiceCustomActions[]>>();
  const [resetWarningOpen, setResetWarningOpen] = useState(false);

  // Fetch project data
  const { data: projectData } = useGetProject({ ...params });

  // Fetch service types list
  const { data: serviceTypesList } = useServiceTypes();

  // Mutation hook for triggering custom action
  const { mutate: triggerCustomAction, isLoading: isTriggeringCustomAction } = useTriggerCustomAction();

  // USE EFFECTS

  // Effect to set form field values based on project data
  useEffect(() => {
    if (!projectData) {
      return;
    }
    form.setFieldValue("title", projectData.title);
    projectData.services.forEach((service) => {
      if (service.service_type === SERVICE_TYPE_SIMPLIFIED.ANALYTICS) return;
      Object.entries(service.settings).forEach(([k, v]) => {
        form.setFieldValue(`${service.service_type}.${k}`, v);
      });
    });
  }, [form, projectData]);

  // Effect to update form field values when project title changes
  useEffect(() => {
    if (projectData?.title) {
      form.setFieldsValue({
        title: projectData?.title,
      });
    }
  }, [form, projectData?.title]);

  // Effect to set fetched settings and custom actions based on project type
  useEffect(() => {
    if (projectData && serviceTypesList && serviceTypesList.length) {
      const _settings: SettingsFormStructuredData = {};
      const _actions: Record<string, IServiceCustomActions[]> = {};
      projectData?.services.forEach((service) => {
        if (service.service_type === SERVICE_TYPE_SIMPLIFIED.ANALYTICS) {
          return;
        }
        _settings[service.service_type] = { settings: [], api_keys: [] };

        const foundServiceType = serviceTypesList.find((serviceType) => serviceType.key === service.service_type);

        const foundServiceTypeSettings = foundServiceType?.settings;
        const foundServiceTypeCustomActions = foundServiceType?.custom_actions;
        const foundServiceTypeApiKeys = service.api_keys;

        if (foundServiceTypeCustomActions?.length) {
          _actions[service.service_type] = foundServiceTypeCustomActions.filter((customAction) => customAction.display);
        }

        if (foundServiceTypeSettings?.length) {
          _settings[service.service_type].settings = foundServiceTypeSettings;
          _settings[service.service_type].api_keys = foundServiceTypeApiKeys;
        }
      });

      setFetchedSettings(_settings);
      setCustomActions(_actions);
    }
  }, [projectData, serviceTypesList]);

  /**
   * Callback function for form submission.
   *
   * @param {Object} values - Form values
   */

  /**
   * Render custom action buttons.
   *
   * @returns {JSX.Element[]}
   */
  const renderCustomActions = (key: string): JSX.Element[] | null => {
    if (!customActions) {
      return null;
    }

    return (
      customActions[key]?.map((action) => (
        <>
          <Button
            className="mr-3 mb-3"
            onClick={() => {
              if (action.action === 'reset_cache') {
                return setResetWarningOpen(true);
              }
              triggerCustomAction({
                ...params,
                serviceKey: key,
                action: action.action,
                params: [...action.params],
              });
            }}
            loading={isTriggeringCustomAction}
            type="default"
            key={action.label}
          >
            {action.label}
          </Button>
          {
            action.action === 'reset_cache' ? (
              <Modal 
                centered 
                open={resetWarningOpen}
                closable={false}
                onCancel={() => setResetWarningOpen(false)}
                title={action.label}
                okText={t('common.reset')}
                onOk={() => {
                  triggerCustomAction({
                    ...params,
                    serviceKey: key,
                    action: action.action,
                    params: [...action.params],
                  });
                  setResetWarningOpen(false);
                }}
              >
                {t('project_single.reset_description')}
              </Modal>
            ) : null
          }
        </>
      )) ?? null
    );
  };

  return (
    <>
      <Form
        name="project-service-form"
        labelCol={{ span: AUTH_FORM_LABEL_COL }}
        labelAlign="left"
        wrapperCol={{ span: AUTH_FORM_WRAPPER_COL }}
        autoComplete="off"
        form={form} // Add the form instance
      >
        {fetchedSettings &&
          Object.entries(fetchedSettings)?.map(([settingKey, value]) => {
            const serviceTitle = serviceTypesList?.find((service) => service.key === settingKey)?.title || settingKey;
            return (
              <Card key={`${settingKey}_card`}>
                {/* <div className="container"> */}
                <Row gutter={16}>
                  {/* first col */}
                  <Col md={17} xs={24}>
                    <Typography.Paragraph strong>
                      {t("project_single.settings")}
                      <Tooltip title={t("project_single.settings_tooltip")}>
                        <InfoCircleTwoTone className="ml-2" />
                      </Tooltip>
                    </Typography.Paragraph>
                    <SettingsFormElements editable={false} settingsKey={settingKey} settings={value.settings} />
                  </Col>
                  {/* first col */}
                  {/* second col */}
                  <Col md={6} offset={isScreenBelow('md') ? 0: 1} xs={24}>
                    <InfoCard
                      className="mb-3"
                      title={t("project_single.limits")}
                      tooltip={t("project_single.limits_tooltip")}
                      info={[
                        {
                          label: t("project_single.max_requests"),
                          value: (projectData?.services.find((service) => service.service_type === SERVICE_TYPE_SIMPLIFIED.CACHE) as any)?.settings
                          .requests_per_minute || "Unlimited"
                        }
                      ]}
                      colorBg="#A09FEE"
                      color="#605DEC"
                    ></InfoCard>
                    {(projectData?.services.find((service) => service.service_type === SERVICE_TYPE_SIMPLIFIED.CACHE) as any).metrics ? (
                      <Fragment>
                        <InfoCard
                          className="mb-3"
                          title={t("project_single.status")}
                          tooltip={t("project_single.status_tooltip")}
                          info={[
                            {
                              label: t("project_single.cache_usage"),
                              value: (projectData?.services.find((service) => service.service_type === SERVICE_TYPE_SIMPLIFIED.CACHE) as any).metrics.usage +
                              "%"
                            }
                          ]}
                          colorBg="#2CDDD8"
                          color="#16B9B5"
                        ></InfoCard>
                      </Fragment>
                    ) : null }
                  </Col>
                  {/* second col */}
                </Row>

                {/* CUSTOM ACTIONS */}
                <Row>
                  <Col xs={24}>
                    <Form.Item>
                      <Typography.Paragraph>
                        <strong>{t("project_single.actions")}</strong>
                      </Typography.Paragraph>
                      {renderCustomActions(settingKey)}
                    </Form.Item>
                  </Col>
                </Row>
                {/* ./ CUSTOM ACTIONS */}
                <Row>
                  <Col xs={24}>
                    <FadeDivider />
                  </Col>
                </Row>
                <ApiKeysSettings
                  title={
                    <Typography.Paragraph strong>
                      {t("project_single.api_key_table_title", {
                        serviceTitle: serviceTitle,
                      })}
                      <Tooltip title={t("project_single.api_key_tooltip")}>
                        <InfoCircleTwoTone className="ml-2" />
                      </Tooltip>
                    </Typography.Paragraph>
                  }
                  serviceKey={settingKey}
                  apiKeys={value.api_keys}
                />
              </Card>
            );
          })}
      </Form>
    </>
  );
};
